/**
 * Selos (badges) de apresentação do catálogo (Entrega 3) · redesign R9.
 *
 * Invariante inegociável (régua §2 / WCAG 1.4.1): **cor nunca é o único
 * indicador de estado**. Todo selo combina ÍCONE + TEXTO; a cor é reforço, não
 * a informação. Ícones são `aria-hidden` porque o texto ao lado já é o nome
 * legível. Redesign R9: os ícones passam de SVG artesanal para `lucide-react`
 * (mesma lib já usada no shell/KpiCard/StatusPanel) — puramente visual, os
 * `data-*`/texto de cada selo são preservados.
 *
 * Só apresentação: os selos recebem o estado já decidido pelos módulos puros
 * (availability.ts / field-authority.ts), nunca recalculam regra.
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito (mesmo com `jsx: "react-jsx"`) — necessário para
 * `tsx --test`, ver nota equivalente em `layout/AppShell.tsx`.
 */
import React, { type ReactElement, type ReactNode } from "react";
import { Ban, CheckCircle2, Store, TriangleAlert, type LucideIcon } from "lucide-react";
import type { AvailabilityBucket } from "../catalog/list-view.js";

type Tone = "success" | "warning" | "danger" | "neutral" | "brand";

const TONE_CLASS: Record<Tone, string> = {
  // Borda + texto do próprio tom (contraste >= 3:1 nos tokens); fundo suave.
  success: "border-success text-success",
  warning: "border-warning text-warning",
  danger: "border-danger text-danger",
  brand: "border-brand text-brand-text",
  neutral: "border-border-strong text-body"
};

function Badge({
  tone,
  icon,
  children,
  dataAttrs
}: {
  tone: Tone;
  icon: ReactElement;
  children: ReactNode;
  dataAttrs?: Record<string, string>;
}): ReactElement {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " + TONE_CLASS[tone]
      }
      {...dataAttrs}
    >
      {icon}
      <span>{children}</span>
    </span>
  );
}

/** Ícone do selo, 14px, herda o tom via `currentColor`. */
function icon(Icon: LucideIcon): ReactElement {
  return <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />;
}

const AVAILABILITY_META: Record<AvailabilityBucket, { tone: Tone; label: string; Icon: LucideIcon }> = {
  disponivel: { tone: "success", label: "Disponível", Icon: CheckCircle2 },
  critico: { tone: "warning", label: "Estoque crítico", Icon: TriangleAlert },
  indisponivel: { tone: "danger", label: "Indisponível", Icon: Ban }
};

export interface AvailabilityBadgeProps {
  bucket: AvailabilityBucket;
  /** Texto adicional (ex.: a conta "4 (6 − 2 reservado)") exibido após o selo. */
  detail?: ReactNode;
  /** Valor consolidado para o hook `data-availability` (preservado nos testes). */
  consolidated: number | null;
  critical: boolean;
}

/**
 * Selo de disponibilidade CONSOLIDADA (ícone + texto). `data-availability` e
 * `data-critical` ficam no wrapper — os mesmos hooks que os testes verificam.
 */
export function AvailabilityBadge({ bucket, detail, consolidated, critical }: AvailabilityBadgeProps): ReactElement {
  const meta = AVAILABILITY_META[bucket];
  return (
    <span
      className="inline-flex flex-col gap-1"
      data-availability={consolidated === null ? "indisponivel" : String(consolidated)}
      data-critical={critical ? "true" : "false"}
    >
      <Badge tone={meta.tone} icon={icon(meta.Icon)}>
        {meta.label}
      </Badge>
      {detail ? <span className="text-xs text-muted">{detail}</span> : null}
    </span>
  );
}

export interface PublishedBadgeProps {
  published: boolean;
}

/** Publicado na loja: selo ícone+texto (sim/não), nunca só cor. */
export function PublishedBadge({ published }: PublishedBadgeProps): ReactElement {
  return published ? (
    <Badge tone="brand" icon={icon(Store)}>
      Publicado
    </Badge>
  ) : (
    <Badge tone="neutral" icon={icon(Ban)}>
      Não publicado
    </Badge>
  );
}

export interface DivergenceBadgeProps {
  pending: number;
}

/** Divergências a tratar: selo de atenção quando há pendência; neutro quando zero. */
export function DivergenceBadge({ pending }: DivergenceBadgeProps): ReactElement {
  return pending > 0 ? (
    <Badge tone="warning" icon={icon(TriangleAlert)} dataAttrs={{ "data-pending-divergences": String(pending) }}>
      {`${pending} item(ns)`}
    </Badge>
  ) : (
    <Badge tone="success" icon={icon(CheckCircle2)} dataAttrs={{ "data-pending-divergences": "0" }}>
      Nenhuma
    </Badge>
  );
}
